import { useEffect, useRef, useState } from 'react';

import {
  getAisWebsocketToken,
  getAisWebsocketUrl,
} from '../../../api/internal-ais-api/internal-ais-api';
import {
  intialiseAisApiWebsocket,
  locationMessagesToHistoricVesselData,
} from '../../../api/internal-ais-api/internal-ais-api-helpers';
import {
  WEBSOCKET_SLOW_ERROR,
  WEBSOCKET_SLOW_WARNING,
} from '../../../api/web-socket/WebSocket';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { EDossiers, setSelectedDossier } from '../../../main-menu/menu.slice';
import MapHelpers from '../../../map/map.utils';
import { LocationMessage } from '../../../models/vessels/internal-ais-api.model';
import store from '../../../store';
import {
  buildIdentifiers,
  getRawVesselPoints,
  renderVesselHistory,
} from '../../../utils/vessels.utils';
import { HistoryFormValues } from '../history-form/history-form-validators';
import {
  clearVesselHistoryData,
  prependVesselHistoryData,
  setError,
  setLoading,
} from '../history-panel.slice';
import VesselHistoryController from '../vessel-history-controller.utils';

interface AisApiWebsocketMessage {
  data?: LocationMessage[];
  metadata?: {
    endOfData?: boolean;
    status?: string;
  };
}

const CALLER_FEATURE = 'ais-api-history';

// ── Pure helpers ──────────────────────────────────────────────────────────

function parseIdentifiers(raw: string) {
  const whiteSpaceRegex = /\s|\r|\n/g;
  const tokens = raw.split(whiteSpaceRegex).filter((s) => s !== '');
  return buildIdentifiers(tokens);
}

// ── Countdown helpers ─────────────────────────────────────────────────────

function useSlowConnectionCountdowns(onWarn: () => void, onError: () => void) {
  const warnTimeout = useRef<NodeJS.Timeout | null>(null);
  const errorTimeout = useRef<NodeJS.Timeout | null>(null);

  const restart = () => {
    if (warnTimeout.current) clearTimeout(warnTimeout.current);
    if (errorTimeout.current) clearTimeout(errorTimeout.current);
    warnTimeout.current = setTimeout(onWarn, WEBSOCKET_SLOW_WARNING);
    errorTimeout.current = setTimeout(onError, WEBSOCKET_SLOW_ERROR);
  };

  const clear = () => {
    if (warnTimeout.current) clearTimeout(warnTimeout.current);
    if (errorTimeout.current) clearTimeout(errorTimeout.current);
    warnTimeout.current = null;
    errorTimeout.current = null;
  };

  return { restart, clear };
}

// ── Public interface ──────────────────────────────────────────────────────

export interface UseHistoryAisApiResult {
  /** Whether a websocket search is currently active. */
  isActive: boolean;
  /** True while the websocket connection is loading slowly (warn threshold). */
  isLoadingSlowly: boolean;
  /** Called when the user submits the search form. */
  onSubmit: (values: HistoryFormValues) => Promise<void>;
  /** True when the latest completed request returned no results. */
  noResults: boolean;
  /** Called when the user cancels an in-progress search. */
  onCancel: () => void;
  /** Props to spread onto the <WebSocket> component when `isActive` is true. */
  websocketHandlers: {
    onConnectionOpened: () => void;
    onConnectionClosed: () => void;
    onConnectionError: () => void;
    onMessageReceived: (event: MessageEvent<unknown>) => void;
  };
  /** Websocket URL with token baked in, set once auth succeeds. */
  websocketUrl: string | undefined;
}

// ── Hook ──────────────────────────────────────────────────────────────────

/**
 * Encapsulates all data-fetching and websocket orchestration for the AIS API
 * history search. The component only handles presentation and Formik setup.
 */
export default function useHistoryAisApi(): UseHistoryAisApiResult {
  const dispatch = useAppDispatch();
  const { formValues } = useAppSelector((state) => state.historyPanel);
  const userPreferences = useAppSelector(
    (state) => state.userPreferences.userPreferences
  );

  const [isActive, setIsActive] = useState(false);
  const [isLoadingSlowly, setIsLoadingSlowly] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [wsBaseUrl, setWsBaseUrl] = useState<string | undefined>(undefined);
  const [wsToken, setWsToken] = useState<string | undefined>(undefined);
  const [websocketUrl, setWebsocketUrl] = useState<string | undefined>(
    undefined
  );

  const hasZoomedToPointsRef = useRef(false);
  const hasReceivedResultsRef = useRef(false);
  const dispatchQueryRef = useRef<(() => Promise<boolean>) | null>(null);

  // Stable dispatch ref so timeout callbacks don't close over stale dispatch.
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  useEffect(
    () => () => {
      dispatchRef.current(setError(false));
    },
    []
  );

  useEffect(() => {
    setNoResults(false);
  }, [
    formValues.identifiers,
    formValues.fromDate,
    formValues.toDate,
    formValues.sampleRate,
  ]);

  const { restart: restartCountdowns, clear: clearCountdowns } =
    useSlowConnectionCountdowns(
      () => setIsLoadingSlowly(true),
      () => {
        setIsLoadingSlowly(false);
        dispatchRef.current(setError(true));
        setIsActive(false);
      }
    );

  // ── Websocket event handlers ────────────────────────────────────────────

  const onConnectionOpened = () => {
    dispatch(setLoading('Loading points...'));
    dispatchQueryRef.current?.();
  };

  const onConnectionClosed = () => {
    dispatch(setLoading(false));
    setIsActive(false);
    clearCountdowns();
  };

  const onConnectionError = () => {
    dispatch(setLoading(false));
    setIsActive(false);
    dispatch(setError(true));
    clearCountdowns();
  };

  const onMessageReceived = (event: MessageEvent<unknown>) => {
    try {
      const response: AisApiWebsocketMessage = JSON.parse(event.data as string);

      if (response.metadata?.endOfData) {
        setIsActive(false);
        clearCountdowns();

        if (hasReceivedResultsRef.current && MapHelpers.isInGlobeView()) {
          VesselHistoryController.onVesselHistoryDraw();
          hasZoomedToPointsRef.current = true;
        }

        dispatch(setLoading(false));

        if (hasReceivedResultsRef.current) {
          store.dispatch(setSelectedDossier(EDossiers.HISTORY));
        } else {
          setNoResults(true);
        }
        return;
      }

      if (!response.data) {
        restartCountdowns();
        return;
      }

      restartCountdowns();

      const converted = locationMessagesToHistoricVesselData(response.data);
      if (converted.length === 0) return;

      hasReceivedResultsRef.current = true;
      dispatch(prependVesselHistoryData({ data: converted, formValues }));
      const rawVesselPoints = getRawVesselPoints(converted);
      renderVesselHistory(rawVesselPoints, userPreferences, {
        prepend: true,
        shouldDisplayOtherVessels: false,
      });

      if (!hasZoomedToPointsRef.current) {
        VesselHistoryController.onVesselHistoryDraw();
        hasZoomedToPointsRef.current = true;
      }
    } catch {
      dispatch(setLoading(false));
      dispatch(setError(true));
      setIsActive(false);
      clearCountdowns();
    }
  };

  // ── Submit / cancel ─────────────────────────────────────────────────────

  const onSubmit = async (values: HistoryFormValues) => {
    setIsLoadingSlowly(false);
    setNoResults(false);
    hasReceivedResultsRef.current = false;
    dispatch(setLoading('Initialising history request...'));
    dispatch(clearVesselHistoryData());
    VesselHistoryController.clearAllHistoryLayers();
    hasZoomedToPointsRef.current = false;
    MapHelpers.returnToGlobeView();

    const { mmsis, imos } = parseIdentifiers(values.identifiers);

    try {
      let baseUrl = wsBaseUrl;
      if (!baseUrl) {
        const urlResponse = await getAisWebsocketUrl();
        baseUrl = urlResponse.ws_url;
        setWsBaseUrl(baseUrl);
      }

      let token = wsToken;
      if (!token) {
        const tokenResponse = await getAisWebsocketToken({
          callerFeature: CALLER_FEATURE,
        });
        token = tokenResponse.token;
        setWsToken(token);
      }

      const { dispatchQuery } = await intialiseAisApiWebsocket(
        null,
        token,
        CALLER_FEATURE,
        undefined,
        new Date(`${values.fromDate}T00:00:00Z`),
        new Date(`${values.toDate}T23:59:59Z`),
        mmsis.map(Number),
        imos.map(Number),
        values.sampleRate
      );

      dispatchQueryRef.current = dispatchQuery;
      setWebsocketUrl(`${baseUrl}?token=${token}`);
      restartCountdowns();
      setIsActive(true);
    } catch {
      dispatch(setError(true));
      dispatch(setLoading(false));
      clearCountdowns();
    }
  };

  const onCancel = () => {
    setIsActive(false);
    setNoResults(false);
    dispatch(setLoading(false));
  };

  return {
    isActive,
    isLoadingSlowly,
    noResults,
    onSubmit,
    onCancel,
    websocketHandlers: {
      onConnectionOpened,
      onConnectionClosed,
      onConnectionError,
      onMessageReceived,
    },
    websocketUrl,
  };
}
